import { useMemo } from "react";
import Fuse from "fuse.js";

import useAllQueuesQuery from "../../apis/tryst/get-all-queues";
import useGetQueueConfig from "../../apis/tryst/get-queue-config";
import useQueueStore from "../../stores/queue-store";
import RightQueueContainer from "./right-queue-container";
import { getAptsForDoctor } from "../../utils/appointment-utils";
import {
  getFilteredAppointmentList,
  SelectedFilterStripComponentUi,
  useGetFilterOptions,
} from "ui";
import {
  addUhidToAppointment,
  queueFilterInlineSearchKeys,
  queueSortBy,
  useFuseSearch,
} from "@elixir/utils";

const CustomQueueView = ({ viewId }: { viewId: string }) => {
  const { data: queueData, isLoading } = useAllQueuesQuery();
  const queueApts = queueData?.[viewId];

  const { data: queueConfigData } = useGetQueueConfig();
  const queueFilter = useQueueStore((state) => state.queueFilter);
  const setQueueFilter = useQueueStore((state) => state.setQueueFilter);
  const selectedSort = useQueueStore((state) => state.selectedSort);
  const selectedDoctor = useQueueStore((state) => state.selectedDoctor);
  const searchQuery = useQueueStore((state) => state.searchQuery);

  const filteredData = useMemo(() => {
    const filteredAppointments =
      getFilteredAppointmentList({
        appointments: getAptsForDoctor({
          apts: queueApts || [],
          doctor_oid: selectedDoctor?.oid,
        }),
        queueFilter,
        queueType: viewId,
      }) || [];

    const sortedAppointments = queueSortBy(filteredAppointments, selectedSort);

    const apts = addUhidToAppointment(sortedAppointments);

    if (!searchQuery.trim()) {
      return apts;
    }

    const f = new Fuse(apts, {
      location: 0,
      threshold: 0.3,
      distance: 100,
      keys: queueFilterInlineSearchKeys,
    });
    const response = f.search(searchQuery);
    const data = response?.map(({ item }) => item);
    return data;
  }, [queueApts, queueFilter, selectedDoctor?.oid, selectedSort, viewId, searchQuery]);

  const filterOptions = useGetFilterOptions({
    queueData: queueApts,
    tagRecords: queueConfigData?.data?.tags || [],
    queueType: viewId,
  });

  return (
    <>
      <SelectedFilterStripComponentUi
        count={filteredData.length}
        queueFilter={queueFilter}
        setQueueFilter={setQueueFilter}
        filterOptions={filterOptions}
      />
      <RightQueueContainer
        isLoading={isLoading}
        queueLength={filteredData.length || 0}
        loadingShimmerPrefix="double"
        queueType={viewId}
        queueApts={filteredData}
      />
    </>
  );
};

export default CustomQueueView;
